// The Unit page's directory: every unit with the people registered there,
// the occupancy filters with their counts, search, and grouping by block.
// Pure functions over the units and residents lists, so they're cheap to test.

import type { AdminUnit, PublicResident } from '@residenone/shared';

export interface UnitRow {
  unit: AdminUnit;
  /** Active residents, primary first. */
  residents: PublicResident[];
}

/**
 * The filter chips above the map. The UI only tells lived-in from vacant:
 * OWNER_ABSENT (rented out) still exists in the data but shows as occupied.
 * 'unregistered' = lived in, but nobody active in the app.
 */
export type UnitFilter = 'all' | 'OCCUPIED' | 'VACANT' | 'unregistered';

export interface BlockGroup {
  block: string;
  rows: UnitRow[];
  /** Totals for the whole block, whatever the filter shows. */
  units: number;
  occupied: number;
  residents: number;
}

const byNumber = (a: AdminUnit, b: AdminUnit) => a.number.localeCompare(b.number, undefined, { numeric: true });

export function buildUnitRows(units: AdminUnit[], residents: PublicResident[]): UnitRow[] {
  const byKey = new Map<string, PublicResident[]>();
  for (const r of residents) {
    if (r.status !== 'ACTIVE' || r.unit_block === null || r.unit_number === null) continue;
    const key = `${r.unit_block}|${r.unit_number}`;
    byKey.set(key, [...(byKey.get(key) ?? []), r]);
  }
  return units.map((unit) => ({
    unit,
    residents: (byKey.get(`${unit.block}|${unit.number}`) ?? []).sort(
      (a, b) => Number(b.is_primary ?? false) - Number(a.is_primary ?? false) || a.full_name.localeCompare(b.full_name),
    ),
  }));
}

/** Someone lives here (owner or tenant: OWNER_ABSENT means rented out), yet nobody is in the app. */
export const isLivedIn = (unit: AdminUnit) => unit.occupancy !== 'VACANT';
export const isUnregistered = (row: UnitRow) => isLivedIn(row.unit) && row.residents.length === 0;

export function matchesFilter(row: UnitRow, filter: UnitFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'unregistered') return isUnregistered(row);
  return filter === 'VACANT' ? !isLivedIn(row.unit) : isLivedIn(row.unit);
}

/** Unit ("A-1", "A 1", "a1", the label) or a resident's name or email. */
export function matchesSearch(row: UnitRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const { block, number, label } = row.unit;
  const compact = q.replace(/[\s-]/g, '');
  if (`${block}${number}`.toLowerCase() === compact || `${block}${number}`.toLowerCase().startsWith(compact) && compact.length > block.length) return true;
  if (label.toLowerCase().includes(q)) return true;
  return row.residents.some((r) => r.full_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
}

export function filterCounts(rows: UnitRow[]): Record<UnitFilter, number> {
  return {
    all: rows.length,
    OCCUPIED: rows.filter((r) => isLivedIn(r.unit)).length,
    VACANT: rows.filter((r) => !isLivedIn(r.unit)).length,
    unregistered: rows.filter(isUnregistered).length,
  };
}

/**
 * Blocks in order, each with its units in number order. `visible` decides which
 * rows show; a block with none visible is left out. The totals count every unit.
 */
export function groupByBlock(rows: UnitRow[], visible: (row: UnitRow) => boolean): BlockGroup[] {
  const blocks = new Map<string, UnitRow[]>();
  for (const row of rows) blocks.set(row.unit.block, [...(blocks.get(row.unit.block) ?? []), row]);
  return [...blocks.entries()]
    .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))
    .map(([block, all]) => ({
      block,
      rows: all.filter(visible).sort((a, b) => byNumber(a.unit, b.unit)),
      units: all.length,
      occupied: all.filter((r) => isLivedIn(r.unit)).length,
      residents: all.reduce((n, r) => n + r.residents.length, 0),
    }))
    .filter((g) => g.rows.length > 0);
}
